import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface DashboardStats {
  total_companies: number;
  total_users: number;
  total_company_credits: number;
  total_company_used: number;
  total_company_remaining: number;
  total_user_credits: number;
  recent_transactions: number;
  expiring_soon: number;
  recently_expired: number;
  azure_total_cost?: number;
  azure_currency?: string;
  azure_breakdown?: any[];
  total_tokens?: number;
  total_token_cost_usd?: number;
  total_token_cost_inr?: number;
  cost_formula?: any;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    total_companies: 0,
    total_users: 0,
    total_company_credits: 0,
    total_company_used: 0,
    total_company_remaining: 0,
    total_user_credits: 0,
    recent_transactions: 0,
    expiring_soon: 0,
    recently_expired: 0,
    azure_total_cost: 0,
    azure_currency: 'USD',
    total_tokens: 0,
    total_token_cost_usd: 0,
    total_token_cost_inr: 0,
    cost_formula: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [sectionLoading, setSectionLoading] = useState({
    companies: false,
    users: false,
    credits: false,
    azure: false,
    tokens: false,
  });

  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch companies count
      setSectionLoading(prev => ({ ...prev, companies: true }));
      const companiesResponse = await api.get('/super-admin/companies/');
      const companiesCount = companiesResponse.data.count || companiesResponse.data.length;

      // Fetch users count
      setSectionLoading(prev => ({ ...prev, users: true }));
      const usersResponse = await api.get('/super-admin/users/');
      const usersCount = usersResponse.data.count || usersResponse.data.length;

      // Fetch credit summary
      setSectionLoading(prev => ({ ...prev, credits: true }));
      const creditsResponse = await api.get('/super-admin/credit-management/credit_summary/');
      const creditSummary = creditsResponse.data;

      // Fetch Azure Cost
      setSectionLoading(prev => ({ ...prev, azure: true }));
      let azureCost = 0;
      let azureCurrency = 'USD';
      let azureBreakdown: any[] = [];
      try {
        const azureResponse = await api.get('/super-admin/azure-cost/');
        if (azureResponse.data.success) {
          azureCost = azureResponse.data.data.totalCost;
          azureCurrency = azureResponse.data.data.currency;
          azureBreakdown = azureResponse.data.data.breakdown || [];
        }
      } catch (err) {
        console.error('Error fetching azure cost', err);
      }

      // Fetch Token Usage
      setSectionLoading(prev => ({ ...prev, tokens: true }));
      let tokenUsage = 0;
      let tokenCostUsd = 0;
      let tokenCostInr = 0;
      let costFormula = null;
      try {
        const tokenResponse = await api.get('/super-admin/token-usage/');
        if (tokenResponse.data.success) {
          tokenUsage = tokenResponse.data.data.summary.total_tokens;
          tokenCostUsd = tokenResponse.data.data.summary.total_cost_usd;
          tokenCostInr = tokenResponse.data.data.summary.total_cost_inr;
          costFormula = tokenResponse.data.data.cost_formula;
        }
      } catch (err) {
        console.error('Error fetching token usage', err);
      }

      setStats({
        total_companies: companiesCount,
        total_users: usersCount,
        total_company_credits: creditSummary.total_company_credits || 0,
        total_company_used: creditSummary.total_company_used || 0,
        total_company_remaining: creditSummary.total_company_remaining || 0,
        total_user_credits: creditSummary.total_user_credits || 0,
        recent_transactions: creditSummary.recent_transactions || 0,
        expiring_soon: creditSummary.expiring_soon || 0,
        recently_expired: creditSummary.recently_expired || 0,
        azure_total_cost: azureCost,
        azure_currency: azureCurrency,
        azure_breakdown: azureBreakdown,
        total_tokens: tokenUsage,
        total_token_cost_usd: tokenCostUsd,
        total_token_cost_inr: tokenCostInr,
        cost_formula: costFormula,
      });
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      setError(
        error.response?.data?.error || 
        error.message || 
        'Failed to fetch dashboard statistics'
      );
    } finally {
      setLoading(false);
      setSectionLoading({ companies: false, users: false, credits: false, azure: false, tokens: false });
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRefresh = () => {
    fetchStats();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={800} sx={{ letterSpacing: '-0.025em', mb: 0.5 }}>
            Super Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview of system-wide statistics, infrastructure costs, and credit allocations
          </Typography>
        </Box>
        <Tooltip title="Refresh Statistics">
          <IconButton onClick={handleRefresh} color="primary" sx={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2.5, mb: 4 }}>
        {/* Companies Card */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Total Companies
                </Typography>
                <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ mt: 0.5 }}>
                  {sectionLoading.companies ? (
                    <CircularProgress size={24} />
                  ) : (
                    (stats.total_companies || 0).toLocaleString()
                  )}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BusinessIcon sx={{ color: 'var(--primary-700)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Users Card */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Total Users
                </Typography>
                <Typography variant="h4" fontWeight={800} color="secondary.main" sx={{ mt: 0.5 }}>
                  {sectionLoading.users ? (
                    <CircularProgress size={24} />
                  ) : (
                    (stats.total_users || 0).toLocaleString()
                  )}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--secondary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PeopleIcon sx={{ color: 'var(--secondary-700)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Total Credits Card */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Total Credits
                </Typography>
                <Typography variant="h4" fontWeight={800} color="success.main" sx={{ mt: 0.5 }}>
                  {sectionLoading.credits ? (
                    <CircularProgress size={24} />
                  ) : (
                    ((stats.total_company_credits || 0) + (stats.total_user_credits || 0)).toLocaleString()
                  )}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--success-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CreditCardIcon sx={{ color: 'var(--success-600)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Available Credits Card */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.04em">
                  Available Credits
                </Typography>
                <Typography variant="h4" fontWeight={800} color="info.main" sx={{ mt: 0.5 }}>
                  {sectionLoading.credits ? (
                    <CircularProgress size={24} />
                  ) : (
                    ((stats.total_company_remaining || 0) + (stats.total_user_credits || 0)).toLocaleString()
                  )}
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--info-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUpIcon sx={{ color: 'var(--info-600)', fontSize: 28 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Infrastructure & Token Costs Section */}
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Infrastructure & Token Costs
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {/* Azure Cost */}
        <Card 
          sx={{ flex: '1 1 300px', minWidth: '300px', cursor: 'pointer', '&:hover': { boxShadow: 6, transition: '0.3s' } }}
          onClick={() => setCostDialogOpen(true)}
        >
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Azure Infrastructure
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">MTD Cost:</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {sectionLoading.azure ? (
                    <CircularProgress size={20} />
                  ) : (
                    `${stats.azure_currency} ${stats.azure_total_cost?.toFixed(2) || '0.00'}`
                  )}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Token Usage */}
        <Card sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              AI Token Usage
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Total Tokens:</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {sectionLoading.tokens ? (
                    <CircularProgress size={20} />
                  ) : (
                    (stats.total_tokens || 0).toLocaleString()
                  )}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Est. Cost:</Typography>
                <Tooltip
                  title={
                    stats.cost_formula ? (
                      <Box sx={{ p: 1, whiteSpace: 'pre-line' }}>
                        <Typography variant="subtitle2" fontWeight="bold">Cost Formula:</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>{stats.cost_formula.description}</Typography>
                        <Typography variant="subtitle2" fontWeight="bold">Model Rates:</Typography>
                        {Object.entries(stats.cost_formula.models).map(([model, rates]: [string, any]) => (
                          <Typography key={model} variant="body2">
                            • {model}: ₹{rates.input_rate_inr}/1K input, ₹{rates.output_rate_inr}/1K output
                          </Typography>
                        ))}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          USD to INR: ₹{stats.cost_formula.usd_to_inr_rate?.toFixed(2) || '85.00'}
                        </Typography>
                      </Box>
                    ) : 'Cost data unavailable'
                  }
                  arrow
                  placement="top"
                >
                  <Typography variant="body1" fontWeight="medium" color="error.main" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                    {sectionLoading.tokens ? (
                      <CircularProgress size={20} />
                    ) : (
                      `₹${stats.total_token_cost_inr?.toFixed(2) || '0.00'} ($${stats.total_token_cost_usd?.toFixed(2) || '0.00'})`
                    )}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Credit Management Section */}
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Credit Management
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {/* Company Credits Breakdown */}
        <Card sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Company Credits
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Total Credits:</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {(stats.total_company_credits || 0).toLocaleString()}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Used Credits:</Typography>
                <Typography variant="body1" fontWeight="medium" color="error.main">
                  {(stats.total_company_used || 0).toLocaleString()}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Remaining Credits:</Typography>
                <Typography variant="body1" fontWeight="medium" color="success.main">
                  {(stats.total_company_remaining || 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* User Credits */}
        <Card sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              User Credits
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Total Credits:</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {(stats.total_user_credits || 0).toLocaleString()}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Recent Transactions:</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {stats.recent_transactions || 0}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Credit Expiry Section */}
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Credit Expiry Management
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Expiring Soon */}
        <Card sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.expiring_soon || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Credits Expiring Soon
                </Typography>
              </Box>
              <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        {/* Recently Expired */}
        <Card sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {stats.recently_expired || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recently Expired Credits
                </Typography>
              </Box>
              <CancelIcon color="error" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        {/* Credit Usage Trend */}
        <Card sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {stats.recent_transactions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recent Transactions
                </Typography>
              </Box>
              <TrendingDownIcon color="info" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Azure Cost Details Dialog */}
      <Dialog 
        open={costDialogOpen} 
        onClose={() => setCostDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Azure Cost Details</Typography>
          <IconButton onClick={() => setCostDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
            <Box>
              <Typography variant="body2" color="text.secondary">Billing Period: <strong>Month-to-Date</strong></Typography>
              <Typography variant="body2" color="text.secondary">Last Updated: <strong>Recently</strong></Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary">Total Actual Cost</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.azure_total_cost?.toFixed(2)} {stats.azure_currency}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="h6" fontWeight="medium">Service Breakdown</Typography>
            <Tooltip title="Breakdown of charges from Azure Cost Management API">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4, bgcolor: 'background.default' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell align="right"><strong>Cost</strong></TableCell>
                  <TableCell align="right"><strong>% of Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.azure_breakdown && stats.azure_breakdown.length > 0 ? (
                  stats.azure_breakdown.map((item, index) => {
                    const pct = stats.azure_total_cost ? (item.cost / stats.azure_total_cost) * 100 : 0;
                    return (
                      <TableRow key={index}>
                        <TableCell>{item.serviceName}</TableCell>
                        <TableCell align="right">{item.cost.toFixed(2)} {stats.azure_currency}</TableCell>
                        <TableCell align="right">{pct.toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No service breakdown data available.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="h6" fontWeight="medium">AI Model & Token Usage</Typography>
            <Tooltip title="Token consumption measured by internal Observability">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            While granular AI model/deployment data (e.g. per-model costs) is missing from the billing API, we tracked a total of <strong>{(stats.total_tokens || 0).toLocaleString()}</strong> tokens used this month across the platform, with an estimated cost of <strong>₹{stats.total_token_cost_inr?.toFixed(2) || '0.00'}</strong>.
          </Alert>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">Total Platform Tokens:</Typography>
            <Typography variant="body2" fontWeight="bold">{(stats.total_tokens || 0).toLocaleString()}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Typography variant="body2" color="text.secondary">Estimated Token Cost:</Typography>
            <Typography variant="body2" fontWeight="bold">₹{stats.total_token_cost_inr?.toFixed(2) || '0.00'} (${stats.total_token_cost_usd?.toFixed(2) || '0.00'})</Typography>
          </Box>

        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setCostDialogOpen(false)} color="inherit" variant="text">
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
